import type { HTMLAttributes, ReactNode } from 'react';

// Repassa os atributos da div (data-tour, data-testid, aria-*): sem isso o TypeScript aceita
// `data-*` sem reclamar — atributo com hífen não é conferido — e o Card os descartava calado,
// deixando tour e teste sem alvo.
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
}

interface CardHeaderProps {
  /** Título principal (h3) */
  title?: ReactNode;
  /** Slot direito para botões/ações */
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}

interface CardBodyProps {
  /** Remove padding interno (útil para tabelas que vão até a borda) */
  flush?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Card atômico do design system com slot pattern.
 *
 * @example
 * <Card>
 *   <Card.Header
 *     title="Processos"
 *     actions={<Button variant="ghost" icon="pi pi-refresh" />}
 *   />
 *   <Card.Body flush>
 *     <DataTable columns={cols} rows={data} />
 *   </Card.Body>
 * </Card>
 */
function CardRoot({ children, className, ...resto }: CardProps) {
  return (
    <div
      {...resto}
      className={`bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-[var(--shadow-sm)] ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ title, actions, children, className }: CardHeaderProps) {
  return (
    <div
      className={`flex items-center justify-between gap-3 px-[18px] py-[14px] border-b border-[var(--border)] ${className ?? ''}`}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className="m-0 text-sm font-semibold tracking-[-0.005em] text-[var(--fg)]">
            {title}
          </h3>
        )}
        {children}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

function CardBody({ flush = false, children, className }: CardBodyProps) {
  const padding = flush ? '' : 'px-[18px] py-4';
  return <div className={`${padding} ${className ?? ''}`}>{children}</div>;
}

CardRoot.Header = CardHeader;
CardRoot.Body = CardBody;

export default CardRoot;
